import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminProduct, AdminProductVariant } from '@/api/productApi';
import { createBrand, getBrands } from '@/api/brandApi';
import { createCategory, getCategories } from '@/api/categoryApi';
import { formatCurrency } from '@/utils/helpers';
import QuickCreateEntityModal, {
  type QuickCreatedEntity,
  type QuickCreateEntityKind,
} from './QuickCreateEntityModal';

export type ProductStockMode = 'existingVariant' | 'newVariant' | 'newProduct';

export interface ProductStockDraft {
  note: string;
  mode: ProductStockMode;
  productId: number | null;
  productVariantId: number | null;
  productName: string;
  category: QuickCreatedEntity & { isNew: boolean };
  brand: QuickCreatedEntity & { isNew: boolean };
  versionName: string;
  color: string;
  unitCost: number;
  price: number;
  quantity: number;
  currentStock: number;
}

interface ProductStockModalProps {
  products: readonly AdminProduct[];
  onClose: () => void;
  onSubmit: (draft: ProductStockDraft) => void;
}

interface VariantOption {
  product: AdminProduct;
  variant: AdminProductVariant;
}

interface EntityOption extends QuickCreatedEntity {
  isNew: boolean;
}

const normalizeSearch = (value: string): string => value.trim().toLocaleLowerCase('vi-VN');

const getLatestUnitCost = (variant: AdminProductVariant): number => {
  const receipts = [...(variant.purchaseReceipts ?? [])];
  receipts.sort((first, second) => second.receiptId - first.receiptId);
  return receipts[0]?.unitCost ?? 0;
};

const ProductStockModal: React.FC<ProductStockModalProps> = ({ products, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const variantOptions = useMemo<VariantOption[]>(
    () => products.flatMap((product) =>
      (product.productVariants ?? []).map((variant) => ({ product, variant })),
    ),
    [products],
  );
  const availableCategories = useMemo<EntityOption[]>(() => {
    const names = products.map((product) => product.category?.name).filter((name): name is string => Boolean(name));
    return Array.from(new Set(names)).map((name) => ({ name, isNew: false }));
  }, [products]);
  const availableBrands = useMemo<EntityOption[]>(() => {
    const names = products.map((product) => product.brand?.name).filter((name): name is string => Boolean(name));
    return Array.from(new Set(names)).map((name) => ({ name, isNew: false }));
  }, [products]);

  const [mode, setMode] = useState<ProductStockMode>('newProduct');
  const [productQuery, setProductQuery] = useState('');
  const [isProductMenuOpen, setIsProductMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<AdminProduct | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<AdminProductVariant | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<EntityOption[]>(availableCategories);
  const [brandOptions, setBrandOptions] = useState<EntityOption[]>(availableBrands);
  const [categoryName, setCategoryName] = useState('');
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(true);
  const [categoryLoadError, setCategoryLoadError] = useState('');
  const [brandName, setBrandName] = useState('');
  const [isBrandsLoading, setIsBrandsLoading] = useState(true);
  const [brandLoadError, setBrandLoadError] = useState('');
  const [versionName, setVersionName] = useState('');
  const [color, setColor] = useState('');
  const [unitCost, setUnitCost] = useState(0);
  const [price, setPrice] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState('');
  const [quickCreateKind, setQuickCreateKind] = useState<QuickCreateEntityKind | null>(null);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !quickCreateKind) onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [onClose, quickCreateKind]);

  useEffect(() => {
    setCategoryOptions((currentOptions) => {
      const knownNames = new Set(currentOptions.map((option) => option.name));
      return [...currentOptions, ...availableCategories.filter((option) => !knownNames.has(option.name))];
    });
    setBrandOptions((currentOptions) => {
      const knownNames = new Set(currentOptions.map((option) => option.name));
      return [...currentOptions, ...availableBrands.filter((option) => !knownNames.has(option.name))];
    });
  }, [availableBrands, availableCategories]);

  const loadBrands = useCallback(async () => {
    setIsBrandsLoading(true);
    setBrandLoadError('');
    try {
      const brands = await getBrands();
      setBrandOptions((currentOptions) => {
        const apiOptions: EntityOption[] = brands.map((brand) => ({ ...brand, isNew: false }));
        const apiNames = new Set(apiOptions.map((option) => normalizeSearch(option.name)));
        return [
          ...apiOptions,
          ...currentOptions.filter((option) => !apiNames.has(normalizeSearch(option.name))),
        ];
      });
    } catch (error: unknown) {
      setBrandLoadError(error instanceof Error ? error.message : t('admin.products.stock_modal.errors.load_brands'));
    } finally {
      setIsBrandsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadBrands();
  }, [loadBrands]);

  const loadCategories = useCallback(async () => {
    setIsCategoriesLoading(true);
    setCategoryLoadError('');
    try {
      const categories = await getCategories();
      setCategoryOptions((currentOptions) => {
        const apiOptions: EntityOption[] = categories.map((category) => ({ ...category, isNew: false }));
        const apiNames = new Set(apiOptions.map((option) => normalizeSearch(option.name)));
        return [
          ...apiOptions,
          ...currentOptions.filter((option) => !apiNames.has(normalizeSearch(option.name))),
        ];
      });
    } catch (error: unknown) {
      setCategoryLoadError(error instanceof Error ? error.message : t('admin.products.stock_modal.errors.load_categories'));
    } finally {
      setIsCategoriesLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  const normalizedQuery = normalizeSearch(productQuery);
  const matchingVariants = variantOptions.filter(({ product, variant }) => {
    if (!normalizedQuery) return true;
    return normalizeSearch(`${product.name} ${variant.color} ${variant.versionName}`).includes(normalizedQuery);
  }).slice(0, 8);
  const matchingProducts = products.filter((product) =>
    !normalizedQuery || normalizeSearch(product.name).includes(normalizedQuery),
  ).slice(0, 4);

  const clearVariantFields = () => {
    setVersionName('');
    setColor('');
    setUnitCost(0);
    setPrice(0);
    setQuantity(1);
    setFormError('');
  };

  const selectExistingVariant = (product: AdminProduct, variant: AdminProductVariant) => {
    setMode('existingVariant');
    setSelectedProduct(product);
    setSelectedVariant(variant);
    setProductQuery(`${product.name} — ${variant.color} / ${variant.versionName}`);
    setCategoryName(product.category?.name ?? '');
    setBrandName(product.brand?.name ?? '');
    setVersionName(variant.versionName);
    setColor(variant.color);
    setUnitCost(getLatestUnitCost(variant));
    setPrice(variant.price);
    setQuantity(1);
    setFormError('');
    setIsProductMenuOpen(false);
  };

  const selectNewVariant = (product: AdminProduct) => {
    setMode('newVariant');
    setSelectedProduct(product);
    setSelectedVariant(null);
    setProductQuery(product.name);
    setCategoryName(product.category?.name ?? '');
    setBrandName(product.brand?.name ?? '');
    clearVariantFields();
    setIsProductMenuOpen(false);
  };

  const selectNewProduct = () => {
    const proposedName = selectedProduct ? '' : productQuery.trim();
    setMode('newProduct');
    setSelectedProduct(null);
    setSelectedVariant(null);
    setProductQuery(proposedName);
    setCategoryName('');
    setBrandName('');
    clearVariantFields();
    setIsProductMenuOpen(false);
  };

  const changeProductQuery = (value: string) => {
    setProductQuery(value);
    setIsProductMenuOpen(true);
    if (selectedProduct || selectedVariant) {
      setMode('newProduct');
      setSelectedProduct(null);
      setSelectedVariant(null);
      setCategoryName('');
      setBrandName('');
      clearVariantFields();
    }
  };

  const createEntity = async (entity: QuickCreatedEntity) => {
    if (quickCreateKind === 'category') {
      const createdCategory = await createCategory({
        name: entity.name,
        description: entity.description ?? '',
      });
      const newOption: EntityOption = { ...createdCategory, isNew: false };
      setCategoryOptions((currentOptions) => [
        newOption,
        ...currentOptions.filter((option) => normalizeSearch(option.name) !== normalizeSearch(newOption.name)),
      ]);
      setCategoryName(createdCategory.name);
      setCategoryLoadError('');
    } else {
      const createdBrand = await createBrand({ name: entity.name });
      const newOption: EntityOption = { ...createdBrand, isNew: false };
      setBrandOptions((currentOptions) => [
        newOption,
        ...currentOptions.filter((option) => normalizeSearch(option.name) !== normalizeSearch(newOption.name)),
      ]);
      setBrandName(createdBrand.name);
      setBrandLoadError('');
    }
    setQuickCreateKind(null);
  };

  const submitStockForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!note.trim()) {
      setFormError(t('admin.products.stock_modal.validation.note'));
      return;
    }
    const productName = mode === 'newProduct' ? productQuery.trim() : selectedProduct?.name ?? '';
    if (!productName) {
      setFormError(t('admin.products.stock_modal.validation.product'));
      return;
    }
    if (!categoryName || !brandName) {
      setFormError(t('admin.products.stock_modal.validation.catalog'));
      return;
    }
    if (!versionName.trim() || !color.trim()) {
      setFormError(t('admin.products.stock_modal.validation.variant'));
      return;
    }
    if (!Number.isInteger(unitCost) || unitCost < 0 || !Number.isInteger(price) || price < 0 || quantity <= 0) {
      setFormError(t('admin.products.stock_modal.validation.numbers'));
      return;
    }

    const category = categoryOptions.find((option) => option.name === categoryName) ?? { name: categoryName, isNew: false };
    const brand = brandOptions.find((option) => option.name === brandName) ?? { name: brandName, isNew: false };
    onSubmit({
      note: note.trim(),
      mode,
      productId: selectedProduct?.id ?? null,
      productVariantId: selectedVariant?.id ?? null,
      productName,
      category,
      brand,
      versionName: versionName.trim(),
      color: color.trim(),
      unitCost,
      price,
      quantity,
      currentStock: selectedVariant?.inventory?.quantity ?? 0,
    });
  };

  const isExistingSelection = mode === 'existingVariant';
  const isCategoryLocked = mode !== 'newProduct' && Boolean(categoryName);
  const isBrandLocked = mode !== 'newProduct' && Boolean(brandName);
  const currentStock = selectedVariant?.inventory?.quantity ?? 0;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-0 sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="flex h-full w-full flex-col bg-white shadow-2xl sm:h-auto sm:max-h-[94vh] sm:max-w-4xl sm:rounded-[20px]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-stock-title"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-outline-variant px-4 py-4 sm:px-6">
          <div>
            <h2 id="product-stock-title" className="font-h1 text-h2 sm:text-h1 text-on-surface">
              {t('admin.products.stock_modal.title')}
            </h2>
            <p className="mt-1 font-body-sm text-body-sm text-secondary">
              {t('admin.products.stock_modal.subtitle')}
            </p>
          </div>
          <button
            type="button"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-secondary hover:bg-surface-container"
            onClick={onClose}
            aria-label={t('admin.products.stock_modal.close')}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        <form className="flex min-h-0 flex-1 flex-col" onSubmit={submitStockForm}>
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-5 sm:px-6">
            <aside className="flex gap-3 rounded-xl border border-primary/25 bg-primary-fixed/45 p-3 text-on-primary-fixed">
              <span className="material-symbols-outlined shrink-0 text-primary">info</span>
              <div className="font-body-sm text-body-sm">
                <p className="font-label-md text-label-md font-bold">{t('admin.products.stock_modal.help_title')}</p>
                <p className="mt-1">
                  {t('admin.products.stock_modal.help_description')}
                </p>
              </div>
            </aside>

            <div>
              <label htmlFor="stock-note" className="font-label-md text-label-md text-on-surface">
                {t('admin.products.stock_modal.note')} <span className="text-error">*</span>
              </label>
              <textarea
                id="stock-note"
                className="mt-1.5 min-h-20 w-full resize-y rounded-lg border border-outline-variant bg-white px-3 py-2.5 font-body-md text-body-md text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                  setFormError('');
                }}
                placeholder={t('admin.products.stock_modal.note_placeholder')}
                required
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="relative sm:col-span-2">
                <label htmlFor="product-search" className="font-label-md text-label-md text-on-surface">
                  {t('admin.products.stock_modal.product_name')} <span className="text-error">*</span>
                </label>
                <div className="relative mt-1.5">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline">search</span>
                  <input
                    id="product-search"
                    className="h-11 w-full rounded-xl border border-outline-variant bg-white pl-10 pr-10 font-body-md text-body-md focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
                    value={productQuery}
                    onChange={(event) => changeProductQuery(event.target.value)}
                    onFocus={() => setIsProductMenuOpen(true)}
                    placeholder={t('admin.products.stock_modal.product_placeholder')}
                    autoComplete="off"
                    autoFocus
                  />
                  <button
                    type="button"
                    className="absolute right-1 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center text-secondary"
                    onClick={() => setIsProductMenuOpen((isOpen) => !isOpen)}
                    aria-label={t('admin.products.stock_modal.open_products')}
                    aria-expanded={isProductMenuOpen}
                  >
                    <span className="material-symbols-outlined">expand_more</span>
                  </button>
                </div>

                {isProductMenuOpen && (
                  <div className="absolute inset-x-0 top-full z-20 mt-1 max-h-80 overflow-y-auto rounded-xl border border-outline-variant bg-white shadow-xl">
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 border-b border-outline-variant bg-primary-fixed/35 px-4 py-3 text-left hover:bg-primary-fixed"
                      onClick={selectNewProduct}
                    >
                      <span className="material-symbols-outlined text-primary">add_circle</span>
                      <span>
                        <strong className="block font-label-md text-label-md text-primary">{t('admin.products.stock_modal.create_product')}</strong>
                        <span className="font-body-sm text-body-sm text-secondary">
                          {productQuery.trim() ? `“${productQuery.trim()}”` : t('admin.products.stock_modal.enter_product_name')}
                        </span>
                      </span>
                    </button>

                    {matchingVariants.map(({ product, variant }) => (
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-4 border-b border-outline-variant/60 px-4 py-3 text-left hover:bg-surface-container-low"
                        key={`variant-${variant.id}`}
                        onClick={() => selectExistingVariant(product, variant)}
                      >
                        <span className="min-w-0">
                          <strong className="block truncate font-label-md text-label-md text-on-surface">{product.name}</strong>
                          <span className="block truncate font-body-sm text-body-sm text-secondary">{variant.color} · {variant.versionName}</span>
                        </span>
                        <span className="shrink-0 text-right font-body-sm text-body-sm text-secondary">
                          {t('admin.products.stock_modal.stock_count', { count: variant.inventory?.quantity ?? 0 })}
                        </span>
                      </button>
                    ))}

                    {matchingProducts.map((product) => (
                      <button
                        type="button"
                        className="flex w-full items-center gap-3 border-b border-outline-variant/60 px-4 py-3 text-left hover:bg-surface-container-low"
                        key={`new-variant-${product.id}`}
                        onClick={() => selectNewVariant(product)}
                      >
                        <span className="material-symbols-outlined text-primary">add</span>
                        <span>
                          <strong className="block font-label-md text-label-md text-on-surface">{t('admin.products.stock_modal.create_variant')}</strong>
                          <span className="font-body-sm text-body-sm text-secondary">{t('admin.products.stock_modal.for_product', { product: product.name })}</span>
                        </span>
                      </button>
                    ))}

                    {matchingVariants.length === 0 && matchingProducts.length === 0 && (
                      <p className="px-4 py-4 text-center font-body-sm text-body-sm text-secondary">
                        {t('admin.products.stock_modal.no_products')}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-3 py-1 font-label-sm text-label-sm ${
                  mode === 'existingVariant'
                    ? 'bg-[#def7ec] text-[#03543f]'
                    : 'bg-primary-fixed text-on-primary-fixed-variant'
                }`}>
                  {mode === 'existingVariant'
                    ? t('admin.products.stock_modal.mode_existing')
                    : mode === 'newVariant'
                      ? t('admin.products.stock_modal.mode_new_variant')
                      : t('admin.products.stock_modal.mode_new_product')}
                </span>
                <span className="font-body-sm text-body-sm text-secondary">{t('admin.products.stock_modal.auto_code')}</span>
              </div>

              <div>
                <label htmlFor="product-category" className="font-label-md text-label-md text-on-surface">
                  {t('admin.products.stock_modal.category')} <span className="text-error">*</span>
                </label>
                <div className="mt-1.5 flex gap-2">
                  <select
                    id="product-category"
                    className="h-11 min-w-0 flex-1 rounded-xl border border-outline-variant bg-white px-3 font-body-md text-body-md disabled:bg-surface-container disabled:text-secondary"
                    value={categoryName}
                    onChange={(event) => setCategoryName(event.target.value)}
                    disabled={isCategoryLocked || isCategoriesLoading}
                  >
                    <option value="">{isCategoriesLoading ? t('admin.products.stock_modal.loading_categories') : t('admin.products.stock_modal.choose_category')}</option>
                    {categoryOptions.map((option) => <option key={option.id ?? option.name} value={option.name}>{option.name}</option>)}
                  </select>
                  <button
                    type="button"
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-primary text-primary hover:bg-primary-fixed disabled:cursor-not-allowed disabled:border-outline-variant disabled:text-outline"
                    onClick={() => setQuickCreateKind('category')}
                    disabled={isCategoryLocked}
                    aria-label={t('admin.products.stock_modal.create_category')}
                  >
                    <span className="material-symbols-outlined">add</span>
                  </button>
                </div>
                {categoryLoadError && !isCategoryLocked && (
                  <div className="mt-1.5 flex items-start justify-between gap-2" role="alert">
                    <span className="font-body-sm text-body-sm text-error">{categoryLoadError}</span>
                    <button
                      type="button"
                      className="shrink-0 font-label-sm text-label-sm text-primary hover:underline"
                      onClick={() => void loadCategories()}
                    >
                      {t('admin.products.actions.retry')}
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="product-brand" className="font-label-md text-label-md text-on-surface">
                  {t('admin.products.stock_modal.brand')} <span className="text-error">*</span>
                </label>
                <div className="mt-1.5 flex gap-2">
                  <select
                    id="product-brand"
                    className="h-11 min-w-0 flex-1 rounded-xl border border-outline-variant bg-white px-3 font-body-md text-body-md disabled:bg-surface-container disabled:text-secondary"
                    value={brandName}
                    onChange={(event) => setBrandName(event.target.value)}
                    disabled={isBrandLocked || isBrandsLoading}
                  >
                    <option value="">{isBrandsLoading ? t('admin.products.stock_modal.loading_brands') : t('admin.products.stock_modal.choose_brand')}</option>
                    {brandOptions.map((option) => <option key={option.id ?? option.name} value={option.name}>{option.name}</option>)}
                  </select>
                  <button
                    type="button"
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-primary text-primary hover:bg-primary-fixed disabled:cursor-not-allowed disabled:border-outline-variant disabled:text-outline"
                    onClick={() => setQuickCreateKind('brand')}
                    disabled={isBrandLocked}
                    aria-label={t('admin.products.stock_modal.create_brand')}
                  >
                    <span className="material-symbols-outlined">add</span>
                  </button>
                </div>
                {brandLoadError && !isBrandLocked && (
                  <div className="mt-1.5 flex items-start justify-between gap-2" role="alert">
                    <span className="font-body-sm text-body-sm text-error">{brandLoadError}</span>
                    <button
                      type="button"
                      className="shrink-0 font-label-sm text-label-sm text-primary hover:underline"
                      onClick={() => void loadBrands()}
                    >
                      {t('admin.products.actions.retry')}
                    </button>
                  </div>
                )}
              </div>

              <label className="font-label-md text-label-md text-on-surface">
                {t('admin.products.stock_modal.version')} <span className="text-error">*</span>
                <input
                  className="mt-1.5 h-11 w-full rounded-xl border border-outline-variant px-3 font-body-md text-body-md disabled:bg-surface-container disabled:text-secondary"
                  value={versionName}
                  onChange={(event) => setVersionName(event.target.value)}
                  placeholder="128GB, 256GB..."
                  disabled={isExistingSelection}
                />
              </label>

              <label className="font-label-md text-label-md text-on-surface">
                {t('admin.products.stock_modal.color')} <span className="text-error">*</span>
                <input
                  className="mt-1.5 h-11 w-full rounded-xl border border-outline-variant px-3 font-body-md text-body-md disabled:bg-surface-container disabled:text-secondary"
                  value={color}
                  onChange={(event) => setColor(event.target.value)}
                  placeholder={t('admin.products.stock_modal.color_placeholder')}
                  disabled={isExistingSelection}
                />
              </label>

              <label className="font-label-md text-label-md text-on-surface">
                {t('admin.products.stock_modal.cost')} <span className="text-error">*</span>
                <input
                  className="mt-1.5 h-11 w-full rounded-xl border border-outline-variant px-3 font-mono text-mono focus:border-primary focus:outline-none"
                  type="number"
                  min="0"
                  step="1"
                  value={unitCost}
                  onChange={(event) => setUnitCost(Number(event.target.value))}
                />
                <span className="mt-1 block font-body-sm text-body-sm text-secondary">{formatCurrency(unitCost)}</span>
              </label>

              <label className="font-label-md text-label-md text-on-surface">
                {t('admin.products.stock_modal.price')} <span className="text-error">*</span>
                <input
                  className="mt-1.5 h-11 w-full rounded-xl border border-outline-variant px-3 font-mono text-mono focus:border-primary focus:outline-none"
                  type="number"
                  min="0"
                  step="1"
                  value={price}
                  onChange={(event) => setPrice(Number(event.target.value))}
                />
                <span className="mt-1 block font-body-sm text-body-sm text-secondary">{formatCurrency(price)}</span>
              </label>

              <label className="font-label-md text-label-md text-on-surface sm:col-span-2">
                {t('admin.products.stock_modal.quantity')} <span className="text-error">*</span>
                <input
                  className="mt-1.5 h-11 w-full rounded-xl border border-outline-variant px-3 font-mono text-mono focus:border-primary focus:outline-none"
                  type="number"
                  min="1"
                  step="1"
                  value={quantity}
                  onChange={(event) => setQuantity(Number(event.target.value))}
                />
              </label>
            </div>

            {isExistingSelection && (
              <section className="grid gap-3 rounded-lg border border-outline-variant bg-surface-container-low p-4 sm:grid-cols-3">
                <div><span className="font-body-sm text-body-sm text-secondary">{t('admin.products.stock_modal.current_stock')}</span><strong className="mt-1 block font-mono text-h2 text-on-surface">{currentStock}</strong></div>
                <div><span className="font-body-sm text-body-sm text-secondary">{t('admin.products.stock_modal.received_quantity')}</span><strong className="mt-1 block font-mono text-h2 text-primary">+{Math.max(quantity, 0)}</strong></div>
                <div><span className="font-body-sm text-body-sm text-secondary">{t('admin.products.stock_modal.result_stock')}</span><strong className="mt-1 block font-mono text-h2 text-on-surface">{currentStock + Math.max(quantity, 0)}</strong></div>
              </section>
            )}

            {formError && <p className="rounded border border-error/30 bg-error-container/40 px-3 py-2 font-body-sm text-body-sm text-error" role="alert">{formError}</p>}
          </div>

          <footer className="flex shrink-0 flex-col-reverse gap-2 border-t border-outline-variant bg-surface-lowest px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <p className="font-body-sm text-body-sm text-secondary">
              {isExistingSelection && selectedVariant
                ? t('admin.products.stock_modal.existing_api_notice', { id: selectedVariant.id })
                : t('admin.products.stock_modal.new_api_notice')}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="min-h-10 rounded-xl border border-outline-variant bg-white px-4 font-label-md text-label-md text-secondary hover:bg-surface-container-low"
                onClick={onClose}
              >
                {t('admin.products.stock_modal.cancel')}
              </button>
              <button
                type="submit"
                className="min-h-10 rounded-xl bg-primary px-4 font-label-md text-label-md text-on-primary shadow-sm hover:bg-on-primary-fixed-variant"
              >
                {t('admin.products.stock_modal.save')}
              </button>
            </div>
          </footer>
        </form>
      </section>

      {quickCreateKind && (
        <QuickCreateEntityModal
          kind={quickCreateKind}
          onClose={() => setQuickCreateKind(null)}
          onCreate={createEntity}
        />
      )}
    </div>
  );
};

export default ProductStockModal;
